import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, ChevronDown, TrendingUp, BarChart3, ClipboardCheck, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
// @ts-ignore - vite-imagetools - 600px width for 2x retina at 300px display
import logoCornerstone from "@/assets/logo-cornerstone.png?format=webp&quality=85&w=600";

interface HeaderProps {
  alwaysDark?: boolean;
  darkText?: boolean;
  keepLogoColor?: boolean; // Keep logo in original color even on transparent background
}

const services = [
  { 
    label: "M&A", 
    href: "/servicos/ma", 
    icon: TrendingUp,
    description: "Fusões e Aquisições" 
  },
  { 
    label: "Valuation", 
    href: "/servicos/valuation", 
    icon: BarChart3,
    description: "Avaliação de Empresas" 
  },
  { 
    label: "Auditoria", 
    href: "/servicos/auditoria", 
    icon: ClipboardCheck,
    description: "Auditoria de Empresas" 
  },
  { 
    label: "Finance Office", 
    href: "/servicos/finance-office", 
    icon: Briefcase,
    description: "Gestão Financeira" 
  },
];

const Header = ({ alwaysDark = false, darkText = false, keepLogoColor = false }: HeaderProps) => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { label: "Sobre", href: "#sobre" },
    { label: "Serviços", href: "#servicos", hasDropdown: true },
    { label: "Equipe", href: "#equipe" },
    { label: "Transações", href: "#transacoes" },
    { label: "Blog", href: "/blog", isRoute: true },
    { label: "Contato", href: "#contato" },
  ];

  const handleNavClick = (href: string, isRoute?: boolean) => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    
    if (isRoute) {
      window.location.href = href;
      return;
    }
    
    // If we're not on the landing page and clicking an anchor link, go to landing page with anchor
    if (!isLandingPage && href.startsWith('#')) {
      window.location.href = '/' + href;
      return;
    }
    
    // Smooth scroll after drawer closes
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // If alwaysDark is true, use dark styling regardless of scroll
  const useDarkStyle = alwaysDark || !isScrolled;
  
  // Text color for dark text mode (for light backgrounds - using Chathams Blue palette)
  const textColorClass = darkText && useDarkStyle ? "text-[#1C4765]" : (!useDarkStyle ? "text-foreground" : "text-primary-foreground");

  return (
    <>
      <header
        className={cn(
          "site-header fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ease-out",
          !useDarkStyle
            ? "bg-card/95 backdrop-blur-md shadow-lg py-3"
            : "bg-transparent py-6",
          // On internal pages mobile, always show background
          !isLandingPage && "max-lg:bg-card/95 max-lg:backdrop-blur-md max-lg:shadow-lg max-lg:py-3",
          isMobileMenuOpen && "lg:z-[100] z-[105]"
        )}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          <a href="/" className="flex items-center h-[48px] sm:h-[60px] md:h-[72px] lg:h-[94px]">
            {/* Single logo with CSS filter for inversion - width/height match mobile size to prevent CLS */}
            <img 
              src={logoCornerstone} 
              alt="Cornerstone Partners" 
              width={210}
              height={48}
              style={{ aspectRatio: '412/94' }}
              className={cn(
                "h-[48px] sm:h-[60px] md:h-[72px] lg:h-[94px] w-auto transition-all duration-500 ease-out",
                // Apply invert filter when on dark background (landing page hero before scroll)
                useDarkStyle && !darkText && !keepLogoColor && isLandingPage && "brightness-0 invert"
              )}
            />
          </a>

          <>
            <nav className="hidden lg:flex items-center gap-8">
                {navItems.map((item) => (
                  item.hasDropdown ? (
                    <div 
                      key={item.label}
                      className="relative"
                      onMouseEnter={() => setIsServicesOpen(true)}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      <button
                        className={cn(
                          "text-base font-medium tracking-wide transition-all duration-300 relative group flex items-center gap-1",
                          textColorClass,
                          darkText && useDarkStyle && "hover:text-[#24567A]"
                        )}
                      >
                        {item.label}
                        <ChevronDown className={cn(
                          "h-4 w-4 transition-transform duration-200",
                          isServicesOpen && "rotate-180"
                        )} />
                        {darkText && useDarkStyle && (
                          <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[rgba(84,174,240,0.45)] group-hover:w-full transition-all duration-300" />
                        )}
                      </button>
                      
                      {/* Dropdown Menu */}
                      <div 
                        className={cn(
                          "absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-200",
                          isServicesOpen 
                            ? "opacity-100 visible translate-y-0" 
                            : "opacity-0 invisible -translate-y-2"
                        )}
                      >
                        <div 
                          className="bg-white rounded-xl shadow-xl border border-[rgba(36,86,122,0.12)] p-3 min-w-[320px]"
                          style={{
                            boxShadow: '0 20px 40px rgba(11,35,53,0.15), 0 8px 16px rgba(11,35,53,0.08)'
                          }}
                        >
                          <div className="grid gap-1">
                            {services.map((service) => {
                              const IconComponent = service.icon;
                              return (
                                <a
                                  key={service.label}
                                  href={service.href}
                                  className="flex items-center gap-3 p-3 rounded-lg transition-all duration-200 hover:bg-[rgba(36,86,122,0.06)] group/item"
                                >
                                  <div 
                                    className="flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 group-hover/item:scale-105"
                                    style={{
                                      background: 'linear-gradient(135deg, rgba(36,86,122,0.08) 0%, rgba(36,86,122,0.04) 100%)',
                                      border: '1px solid rgba(36,86,122,0.1)'
                                    }}
                                  >
                                    <IconComponent className="w-5 h-5 text-[#24567A]" />
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-sm font-semibold text-[#0B2335] group-hover/item:text-[#24567A] transition-colors">
                                      {service.label}
                                    </span>
                                    <span className="text-xs text-[#5A81A0]">
                                      {service.description}
                                    </span>
                                  </div>
                                </a>
                              );
                            })}
                          </div>
                          
                          {/* Ver todos link */}
                          <div className="mt-2 pt-2 border-t border-[rgba(36,86,122,0.08)]">
                            <a
                              href="#servicos"
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavClick('#servicos');
                              }}
                              className="flex items-center justify-center gap-2 py-2 text-sm font-medium text-[#24567A] hover:text-[#0B2335] transition-colors"
                            >
                              Ver todos os serviços
                              <ChevronRight className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={item.isRoute ? undefined : (e) => {
                        e.preventDefault();
                        handleNavClick(item.href, item.isRoute);
                      }}
                      className={cn(
                        "text-base font-medium tracking-wide transition-all duration-300 relative group",
                        textColorClass,
                        darkText && useDarkStyle && "hover:text-[#24567A]"
                      )}
                    >
                      {item.label}
                      {darkText && useDarkStyle && (
                        <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[rgba(84,174,240,0.45)] group-hover:w-full transition-all duration-300" />
                      )}
                    </a>
                  )
                ))}
                {!useDarkStyle && (
                  <Button 
                    variant="outline" 
                    className="ml-4 transition-all duration-300 border-primary text-primary hover:bg-primary hover:text-primary-foreground animate-fade-in"
                    asChild
                  >
                    <a href="#contato">Fale Conosco</a>
                  </Button>
                )}
              </nav>

              {isLandingPage && (
                <button
                  className="lg:hidden"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
                >
                  {isMobileMenuOpen ? (
                    <X className={cn("h-6 w-6", textColorClass)} />
                  ) : (
                    <Menu className={cn("h-6 w-6", textColorClass)} />
                  )}
                </button>
              )}
          </>
        </div>
      </header>

      {/* Mobile Menu Drawer - Only on landing page */}
      {isLandingPage && (
        <>
        {/* Overlay */}
        <div
          className={cn(
            "fixed inset-0 z-[60] lg:hidden transition-all duration-300",
            isMobileMenuOpen 
              ? "opacity-100 pointer-events-auto" 
              : "opacity-0 pointer-events-none"
          )}
          style={{
            backgroundColor: 'rgba(2,18,38,0.55)',
            backdropFilter: isMobileMenuOpen ? 'blur(4px)' : 'none',
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />

      {/* Drawer */}
      <div 
        className={cn(
          "fixed top-0 right-0 bottom-0 z-[70] lg:hidden transition-transform duration-300 ease-out flex flex-col",
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
        style={{
          width: '86%',
          maxWidth: '380px',
          backgroundColor: '#FBFCFE',
          borderLeft: '1px solid rgba(2,18,38,0.08)',
          boxShadow: '-8px 0 32px rgba(2,18,38,0.12)',
        }}
      >
            {/* Drawer Header */}
            <div 
              className="flex items-center justify-between px-6"
              style={{
                height: '76px',
                background: 'linear-gradient(135deg, #0B2335 0%, #24567A 100%)',
              }}
            >
              <img 
                src={logoCornerstone} 
                alt="Cornerstone Partners" 
                width={158}
                height={36}
                className="h-9 w-auto brightness-0 invert"
              />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                }}
                aria-label="Fechar menu"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 flex flex-col px-6 py-4 overflow-y-auto">
              {navItems.map((item, index) => (
                item.hasDropdown ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      className="flex items-center justify-between w-full text-left transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700/50 focus-visible:ring-offset-2 rounded-lg"
                      style={{
                        minHeight: '54px',
                        padding: '0 4px',
                        borderBottom: !isServicesOpen ? '1px solid rgba(2,18,38,0.06)' : 'none',
                      }}
                    >
                      <span 
                        className="font-medium transition-colors duration-200 group-hover:text-brand-700 group-active:text-brand-700"
                        style={{
                          fontSize: '20px',
                          fontWeight: 500,
                          color: '#0B2335',
                        }}
                      >
                        {item.label}
                      </span>
                      <ChevronDown 
                        className={cn(
                          "h-5 w-5 transition-all duration-200",
                          isServicesOpen ? "rotate-180 text-brand-700" : ""
                        )}
                        style={{ 
                          color: isServicesOpen ? '#24567A' : 'rgba(11,35,53,0.35)',
                        }}
                      />
                    </button>
                    
                    {/* Services Submenu */}
                    <div 
                      className={cn(
                        "overflow-hidden transition-all duration-300",
                        isServicesOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
                      )}
                    >
                      <div 
                        className="py-2 mb-2"
                        style={{
                          borderBottom: '1px solid rgba(2,18,38,0.06)',
                        }}
                      >
                        {services.map((service) => {
                          const IconComponent = service.icon;
                          return (
                            <a
                              key={service.label}
                              href={service.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center gap-3 py-3 px-2 rounded-lg transition-all duration-200 active:bg-[rgba(36,86,122,0.06)]"
                            >
                              <div 
                                className="flex items-center justify-center w-9 h-9 rounded-lg"
                                style={{
                                  background: 'linear-gradient(135deg, rgba(36,86,122,0.1) 0%, rgba(36,86,122,0.05) 100%)',
                                  border: '1px solid rgba(36,86,122,0.1)'
                                }}
                              >
                                <IconComponent className="w-4 h-4 text-[#24567A]" />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-base font-medium text-[#0B2335]">
                                  {service.label}
                                </span>
                                <span className="text-xs text-[#5A81A0]">
                                  {service.description}
                                </span>
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item.href, item.isRoute)}
                    className="flex items-center justify-between w-full text-left transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700/50 focus-visible:ring-offset-2 rounded-lg"
                    style={{
                      minHeight: '54px',
                      padding: '0 4px',
                      borderBottom: index < navItems.length - 1 ? '1px solid rgba(2,18,38,0.06)' : 'none',
                    }}
                  >
                    <span 
                      className="font-medium transition-colors duration-200 group-hover:text-brand-700 group-active:text-brand-700"
                      style={{
                        fontSize: '20px',
                        fontWeight: 500,
                        color: '#0B2335',
                      }}
                    >
                      {item.label}
                    </span>
                    <ChevronRight 
                      className="h-5 w-5 transition-all duration-200 group-hover:translate-x-1 group-hover:text-brand-700"
                      style={{ 
                        color: 'rgba(11,35,53,0.35)',
                      }}
                    />
                  </button>
                )
              ))}
            </nav>

            {/* Footer CTA */}
            <div className="px-6 pb-8 pt-4">
              <button
                onClick={() => handleNavClick('#contato')}
                className="w-full flex items-center justify-center font-medium transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                style={{
                  maxWidth: '340px',
                  margin: '0 auto',
                  height: '50px',
                  backgroundColor: '#24567A',
                  color: '#FFFFFF',
                  borderRadius: '14px',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                Fale Conosco
              </button>
              <p 
                className="text-center mt-4"
                style={{
                  fontSize: '13px',
                  color: 'rgba(2,18,38,0.55)',
                }}
              >
                Londrina • PR
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
